import { useState, type ReactNode } from 'react';
import { PageHeader, type PageHeaderAction } from './PageHeader';
import { Card } from './Card';
import { Skeleton } from './Skeleton';
import { ErrorState } from './ErrorState';
import { EmptyState } from './EmptyState';
import { Table, type CardRowActions, type Column } from './Table';
import { SearchInput } from './SearchInput';
import { FilterBar, type ActiveFilter } from './FilterBar';
import { FilterSheet } from './FilterSheet';
import { Pagination } from './Pagination';
import type { ListQueryStatus, SortDir } from './useListQuery';
import { listIdFromTitle, useColumnWidths } from './useColumnWidths';

export interface DataListShellProps<T> {
  // Header
  title: string;
  /** Overrides the default `"{totalCount} {itemLabel(Plural)}"` description. */
  description?: string;
  itemLabel?: string;
  /** Defaults to a simple English plural of `itemLabel` (consonant+y → "ies", otherwise
   *  "+s"). Pass it explicitly for anything irregular ("person" → "people"). */
  itemLabelPlural?: string;
  headerAction?: ReactNode;
  /** Forwarded to PageHeader `primaryAction` (#22197, BR-MOB-006) — stays visible at every width. */
  headerPrimaryAction?: ReactNode;
  /** Forwarded to PageHeader `secondaryActions` (#22197, BR-MOB-006) — behind ⋯ on a phone. */
  headerSecondaryActions?: PageHeaderAction[];

  // Toolbar
  /** Search box is rendered only when `onSearchChange` is supplied — omit both for
   *  list endpoints that have no text search (e.g. a filter-only list). */
  search?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  searchAriaLabel?: string;
  /** Caller-rendered filter controls (e.g. `<select>`s), rendered alongside the search box. */
  toolbarFilters?: ReactNode;
  activeFilters?: ActiveFilter[];
  onRemoveFilter?: (key: string) => void;
  onClearFilters?: () => void;
  /** Defaults to 25 to match the AccountList reference page size. */
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];

  // Phone layout (#22193). Every prop below is OPT-IN and defaults off: the client portal shares
  // this shell and its prototype defines no phone patterns yet, so it must render unchanged.
  /** BR-MOB-002 / BR-MOB-003: below 768px the search box fills the row and the toolbar filters
   *  (and the page-size control) move behind ONE "Filters (n)" button that opens a bottom sheet
   *  with Clear and Apply. From 768px up the filters stay inline exactly as before. */
  mobileFilters?: boolean;
  /** The n in "Filters (n)". Defaults to `activeFilters.length`; pass it for a list whose active
   *  filters are not shown as chips. */
  activeFilterCount?: number;
  /** BR-MOB-004: a status strip (see `StatusStrip`). Inline after the filters from 768px up; its
   *  own full-width, horizontally scrolling row under the search box below 768px. */
  statusStrip?: ReactNode;

  // Table
  columns: Column<T>[];
  data: T[];
  keyField: keyof T;
  onRowClick?: (item: T) => void;
  responsive?: boolean;
  sortBy?: string;
  sortDir?: SortDir;
  onSort?: (key: string, dir: SortDir | undefined) => void;
  /** Optional per-row className (e.g. overdue-row emphasis), forwarded to the Table. */
  rowClassName?: (item: T) => string;
  /** Compact body text — data cells render at text-xs. Defaults to `true` so every list view shares
   *  the invoice-list density (operator 2026-07-14); pass `false` to opt a list back to text-sm. */
  dense?: boolean;
  /** Storage identity for this list's operator-set column widths (#13249). Defaults to a slug of
   *  `title`, which is already unique per list screen. Pass an explicit id when two screens share
   *  a title, or when a title is user-visible copy you expect to reword (renaming the title
   *  otherwise silently orphans the stored widths — a reset, not a bug). */
  listId?: string;
  /** Set `false` to opt a list out of resizable columns entirely (#13249). Defaults to `true`:
   *  resizing is a property of the shared shell, so every list gets it with one behaviour. */
  resizableColumns?: boolean;
  /** BR-MOB-005: opt into phone compact cards (title + status + two key fields + chevron) instead
   *  of the default stacked card. Columns declare their place with `Column.mobile`. */
  compactCards?: boolean;
  /** BR-MOB-005: what tapping a compact card opens. Defaults to `onRowClick`. */
  onCardClick?: (item: T) => void;
  /** BR-MOB-020 (#22206): the row's actions as a 44px menu on each compact card. */
  rowActions?: (item: T) => CardRowActions | null | undefined;
  /** BR-MOB-021 (#22206): `false` = no detail page, so the compact card is inert (no chevron). */
  hasDetailPage?: boolean;

  // Four-state
  status: ListQueryStatus;
  error?: string | null;
  onRetry?: () => void;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;

  // Pagination
  page: number;
  totalCount: number;
  onPageChange: (page: number) => void;
}

/** Default plural for `itemLabel` (#22188): consonant+y → "ies" ("entry" → "entries"),
 *  everything else (vowel+y included: "day" → "days") → "+s". Deliberately file-local —
 *  irregular nouns are the caller's job via `itemLabelPlural`. */
function defaultPlural(label: string): string {
  return /[^aeiou]y$/i.test(label) ? `${label.slice(0, -1)}ies` : `${label}s`;
}

/**
 * The shared admin-list screen shell (#13136) — header (title/count/action) + toolbar
 * (search + caller-supplied filter controls + page-size) + active-filter chips + table
 * (four-state loading/empty/error/data rendering) + pagination. Purely presentational —
 * driven by `useListQuery`'s state + setters (or any equivalent state source).
 *
 * The page-size selector lives once, in the toolbar — the `Pagination` footer rendered
 * here deliberately omits its own `onPageSizeChange` to avoid a duplicate control. Callers
 * using `Pagination` standalone (outside this shell) still get its own page-size affordance.
 *
 * Full-width (#13185): the shell itself has never imposed a max-width — it always
 * renders at 100% of whatever width its container gives it (made explicit below via
 * `w-full`). The "page width is wasted" mockup feedback traced to the *app shell's*
 * content wrapper (`apps/admin-portal/src/layouts/AdminLayout.tsx`), which capped
 * every page at `max-w-7xl`; that cap is removed as part of this Story so list
 * screens (and everything else in the shell) get the full content width. Pages that
 * want a narrower reading width (forms, etc.) already carry their own inner
 * `max-w-*` wrapper — verified across the existing page set before loosening the
 * app-wide cap.
 */
export function DataListShell<T extends object>({
  title,
  description,
  itemLabel = 'item',
  itemLabelPlural = defaultPlural(itemLabel),
  headerAction,
  headerPrimaryAction,
  headerSecondaryActions,
  search,
  onSearchChange,
  searchPlaceholder,
  searchAriaLabel,
  toolbarFilters,
  activeFilters = [],
  onRemoveFilter,
  onClearFilters,
  pageSize = 50,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  mobileFilters = false,
  activeFilterCount,
  statusStrip,
  columns,
  data,
  keyField,
  onRowClick,
  responsive,
  sortBy,
  sortDir,
  onSort,
  rowClassName,
  dense = true,
  listId,
  resizableColumns = true,
  compactCards = false,
  onCardClick,
  rowActions,
  hasDetailPage,
  status,
  error,
  onRetry,
  emptyTitle = `No ${itemLabelPlural} found`,
  emptyDescription,
  emptyAction,
  page,
  totalCount,
  onPageChange,
}: DataListShellProps<T>) {
  const headerDescription = description ?? `${totalCount} ${totalCount === 1 ? itemLabel : itemLabelPlural}`;

  // Column widths (#13249) — per list, per viewer. The hook is called unconditionally (hooks
  // rules); `resizableColumns` decides only whether the wiring reaches the Table, so opting a
  // list out costs nothing and leaves its stored widths intact for if it opts back in.
  const { widths, setWidths } = useColumnWidths(listId ?? listIdFromTitle(title));

  // BR-MOB-002 (#22193) — the phone Filters sheet. Only reachable from the below-md button.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filterCount = activeFilterCount ?? activeFilters.length;
  const hasSheetContent = Boolean(toolbarFilters) || Boolean(onPageSizeChange);

  const pageSizeSelect = (extraClass = '') =>
    onPageSizeChange && (
      <select
        value={pageSize}
        onChange={e => onPageSizeChange(Number(e.target.value))}
        aria-label="Items per page"
        className={`h-9 rounded-md border border-gray-300 px-3 text-sm${extraClass}`}
      >
        {pageSizeOptions.map(size => (
          <option key={size} value={size}>{size} / page</option>
        ))}
      </select>
    );

  return (
    <div className="w-full">
      <PageHeader
        title={title}
        description={headerDescription}
        action={headerAction}
        primaryAction={headerPrimaryAction}
        secondaryActions={headerSecondaryActions}
      />

      <Card padding={false}>
        <div className="p-4 border-b border-gray-200 flex flex-wrap items-center gap-3">
          {onSearchChange && (
            <SearchInput
              value={search ?? ''}
              onChange={onSearchChange}
              placeholder={searchPlaceholder ?? `Search ${itemLabelPlural}...`}
              ariaLabel={searchAriaLabel ?? `Search ${itemLabelPlural}`}
              // BR-MOB-003: fluid below md (the input's own min/max widths are released).
              className={mobileFilters ? 'max-md:flex-1 max-md:min-w-0 max-md:[&_input]:min-w-0 max-md:[&_input]:max-w-none' : ''}
            />
          )}
          {mobileFilters
            ? (
              // Hidden below md (the controls live in the sheet there); `contents` from md up so
              // each control is a flex item of the toolbar exactly as before.
              <div data-testid="toolbar-filters" className="hidden md:contents">{toolbarFilters}</div>
            )
            : toolbarFilters}
          {statusStrip && (
            <div data-testid="status-strip-slot" className="max-md:order-last max-md:basis-full max-md:min-w-0">
              {statusStrip}
            </div>
          )}
          {pageSizeSelect(mobileFilters ? ' max-md:hidden' : '')}
          {mobileFilters && hasSheetContent && (
            <button
              type="button"
              onClick={() => setFiltersOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={filtersOpen}
              className="md:hidden inline-flex min-h-11 shrink-0 items-center whitespace-nowrap rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              {filterCount > 0 ? `Filters (${filterCount})` : 'Filters'}
            </button>
          )}
        </div>

        {activeFilters.length > 0 && (
          <div className="px-4 py-2 border-b border-gray-200" data-testid="active-filter-chips">
            <FilterBar
              filters={activeFilters}
              onRemove={key => onRemoveFilter?.(key)}
              onClearAll={onClearFilters}
              strip={mobileFilters}
            />
          </div>
        )}

        {status === 'loading' && (
          <div className="p-6" aria-label={`Loading ${itemLabelPlural}`}>
            <Skeleton lines={8} className="h-4 w-full" />
          </div>
        )}

        {status === 'error' && <ErrorState message={error ?? 'Something went wrong'} onRetry={onRetry} />}

        {status === 'empty' && (
          <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />
        )}

        {status === 'data' && (
          <>
            <Table<T>
              columns={columns}
              data={data}
              keyField={keyField}
              onRowClick={onRowClick}
              responsive={responsive}
              sortBy={sortBy}
              sortDir={sortDir}
              onSort={onSort}
              rowClassName={rowClassName}
              dense={dense}
              columnWidths={resizableColumns ? widths : undefined}
              onColumnWidthsChange={resizableColumns ? setWidths : undefined}
              compactCards={compactCards}
              onCardClick={onCardClick}
              rowActions={rowActions}
              hasDetailPage={hasDetailPage}
            />
            <Pagination page={page} pageSize={pageSize} totalCount={totalCount} onPageChange={onPageChange} />
          </>
        )}
      </Card>

      {mobileFilters && (
        <FilterSheet open={filtersOpen} onClose={() => setFiltersOpen(false)} onClear={onClearFilters}>
          {toolbarFilters}
          {pageSizeSelect()}
        </FilterSheet>
      )}
    </div>
  );
}
